import authService, { TOKEN_TYPES } from "./authService";

const BASE_API_URL = import.meta.env.VITE_API_URL;

export interface UploadProgressEvent {
  percent: number;
}

export interface UploadResult {
  success: boolean;
  data?: any;
  error?: string;
}

export const fileUploadService = {
  /**
   * Upload a file to the server with progress tracking
   * @param scope - The scope (trial/portfolio)
   * @param trialId - The trial ID for the upload endpoint
   * @param file - The file to upload
   * @param onProgress - Optional callback for upload progress updates
   * @returns Promise resolving to the upload result
   */
  uploadFile: async (
    scope: string,
    trialId: string,
    file: File,
    onProgress?: (event: UploadProgressEvent) => void,
  ): Promise<UploadResult> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress?.({ percent });
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let data;
          try {
            data = xhr.response ? JSON.parse(xhr.response) : null;
          } catch {
            data = xhr.response;
          }
          resolve({ success: true, data });
        } else {
          const errorMsg = `Upload failed with status ${xhr.status}`;
          reject(new Error(errorMsg));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error occurred"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload was aborted"));
      });

      xhr.open(
        "POST",
        `${BASE_API_URL}/data/${scope}/${trialId}/files/upload/`,
      );

      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  },
};
